
import React, { useState, useCallback } from "react";
import { Input, Segmented, Select, Button, Space } from "antd";
import {
  AppstoreOutlined,
  BarsOutlined,
  UploadOutlined,
  FolderAddOutlined,
} from "@ant-design/icons";
import { ViewMode, SortField, SortOrder } from "@/types";
import { SORT_OPTIONS, DEBOUNCE_SEARCH_MS } from "@/utils/constants";

interface ToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  sortBy: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onUploadClick: () => void;
  onNewFolderClick: () => void;
  canUpload?: boolean;
  canCreateFolder?: boolean;
}

export default function Toolbar({
  viewMode,
  onViewModeChange,
  sortBy,
  sortOrder,
  onSortChange,
  searchQuery,
  onSearchChange,
  onUploadClick,
  onNewFolderClick,
  canUpload = true,
  canCreateFolder = true,
}: ToolbarProps) {
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearch(value);

      // Debounce search
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(() => {
        onSearchChange(value);
      }, DEBOUNCE_SEARCH_MS);

      setSearchTimeout(timeout);
    },
    [searchTimeout, onSearchChange]
  );

  const handleSortChange = useCallback(
    (value: string) => {
      // Toggle order if same field, otherwise default to desc
      if (value === sortBy) {
        onSortChange(value as SortField, sortOrder === "asc" ? "desc" : "asc");
      } else {
        onSortChange(value as SortField, "desc");
      }
    },
    [sortBy, sortOrder, onSortChange]
  );

  const viewOptions = [
    { value: "grid", icon: <AppstoreOutlined /> },
    { value: "list", icon: <BarsOutlined /> },
  ];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "12px 0",
      }}
    >
      {/* Left side - Search */}
      <Input.Search
        placeholder="Search files and folders..."
        allowClear
        value={localSearch}
        onChange={handleSearchChange}
        onSearch={(value) => onSearchChange(value)}
        style={{ maxWidth: 320 }}
      />

      {/* Right side - Controls */}
      <Space size="middle">
        {/* View toggle */}
        <Segmented
          options={viewOptions}
          value={viewMode}
          onChange={(value) => onViewModeChange(value as ViewMode)}
        />

        {/* Sort */}
        <Select
          value={sortBy}
          onChange={handleSortChange}
          style={{ minWidth: 150 }}
          popupMatchSelectWidth={false}
          options={SORT_OPTIONS.map((opt) => ({
            label: opt.label,
            value: opt.value,
          }))}
        />

        {/* New folder button */}
        {canCreateFolder && (
          <Button icon={<FolderAddOutlined />} onClick={onNewFolderClick}>
            New folder
          </Button>
        )}

        {/* Upload button */}
        {canUpload && (
          <Button type="primary" icon={<UploadOutlined />} onClick={onUploadClick}>
            Upload
          </Button>
        )}
      </Space>
    </div>
  );
}
